import { apiRequest, createUrlWithCenterId } from '../utils/apiHelpers';
import { sortByNombre } from '../utils/sortByNombre';

export interface Permission {
  id: number;
  name: string;
  description?: string;
  activo: boolean;
}

export interface Cliente {
  id: number;
  numero: number;
  tipoDeClienteId: number;
  tipoDeCliente?: TipoDeCliente;
  tipoDeServicioId: number;
  tipoDeServicio?: TipoDeServicio;
  centroId: number;
  centro?: Center;
  nombre: string;
  cuit: string;
  telefono?: string;
  razonSocial?: string;
  activo: boolean;
  usuarioIds?: number[];
  fechaCreacion: string;
  fechaUpdate?: string;
  usuarioCreacion: string;
  usuarioUpdate?: string;
  alcances?: ClienteAlcance[];
  alcancesDetalles?: ClienteAlcanceDetalle[];
  archivos?: Archivo[];
  clienteArchivos?: ClienteArchivo[];
}

export interface TipoDeCliente {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface TipoDeServicio {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface TipoProducto {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface ClienteAlcance {
  id: number;
  clienteId: number;
  tipoDeProductoId: number;
  tipoDeProducto?: TipoProducto;
  tipoDeElementoId?: number;
  tipoDeElemento?: TipoElemento;
  tipoDeServicioId?: number;
  tipoDeServicio?: TipoDeServicio;
  periodicidadId: number;
  periodicidad?: Periodicidad;
}

export interface ClienteAlcanceDetalle {
  id: number;
  clienteId: number;
  alcanceDetalleId: number;
  alcanceDetalle?: AlcanceDetalle;
}

export interface TipoElemento {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface Periodicidad {
  id: number;
  nombre: string;
  descripcion?: string;
  dias: number;
  activo: boolean;
}

export interface AlcanceDetalle {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

export interface Archivo {
  id: number;
  nombre: string;
  descripcion?: string;
  ruta: string;
  tipo: string;
  tamaño: number;
  activo: boolean;
}

export interface ClienteArchivo {
  id: number;
  clienteId: number;
  archivoId: number;
  archivo?: Archivo;
  cliente?: Cliente;
  activo: boolean;
}

export interface Center {
  id: number;
  nombre: string;
  descripcion?: string;
  activo: boolean;
}

function normalizeCenter(raw: any): Center | undefined {
  if (!raw) return undefined;
  return {
    id: raw.id ?? raw.Id,
    nombre: raw.nombre ?? raw.name ?? raw.Name ?? '',
    descripcion: raw.descripcion ?? raw.description ?? raw.Description,
    activo: raw.activo ?? raw.Activo ?? true,
  };
}

function normalizeCliente(raw: any): Cliente {
  const centro = normalizeCenter(raw.centro || raw.businessCenter || raw.BusinessCenter);
  const usuarioIdsRaw = raw.usuarioIds ?? raw.UsuarioIds;
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    nombre: raw.nombre ?? raw.Nombre ?? '',
    centroId: raw.centroId ?? raw.businessCenterId ?? raw.BusinessCenterId ?? 0,
    centro,
    usuarioIds: Array.isArray(usuarioIdsRaw)
      ? usuarioIdsRaw.map((id: any) => Number(id)).filter((id: number) => !Number.isNaN(id))
      : [],
  };
}

export interface User {
  id: number;
  alias: string;
  email: string;
  activo: boolean;
  roleId: number;
  nombre?: string;
  apellido?: string;
  centers: Center[];
  permissions: Permission[];
}

export const clienteService = {
  // Obtener todos los clientes
  getClientes: async (): Promise<Cliente[]> => {
    const endpoint = createUrlWithCenterId('/api/clientes');
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeCliente));
  },

  getComboList: async (): Promise<{ id: number; nombre: string }[]> => {
    const endpoint = createUrlWithCenterId('/api/clientes/combolist');
    const rows = await apiRequest(endpoint);
    return sortByNombre(
      (rows || []).map((c: any) => ({
        id: c.id ?? c.Id,
        nombre: c.nombre ?? c.Nombre ?? '',
      })),
    );
  },

  searchClientes: async (q: string, centerIds?: number[]): Promise<Cliente[]> => {
    const term = q.trim();
    if (term.length < 3) {
      return [];
    }

    const params = new URLSearchParams({ q: term });
    (centerIds ?? []).forEach((id) => params.append('centerIds', String(id)));
    const rows = await apiRequest(`/api/clientes?${params.toString()}`);
    return sortByNombre((rows || []).map(normalizeCliente));
  },

  // Obtener todos los clientes para administración
  getClientesForAdmin: async (centerId?: number | null): Promise<Cliente[]> => {
    const endpoint = createUrlWithCenterId('/api/clientes/admin', undefined, centerId);
    const rows = await apiRequest(endpoint);
    return sortByNombre((rows || []).map(normalizeCliente));
  },

  // Obtener cliente por ID
  getCliente: async (id: number): Promise<Cliente> => {
    const endpoint = createUrlWithCenterId(`/api/clientes/${id}`);
    return normalizeCliente(await apiRequest(endpoint));
  },

  // Crear cliente
  createCliente: async (
    cliente: Omit<Cliente, 'id' | 'numero' | 'fechaCreacion' | 'usuarioCreacion'>,
  ): Promise<Cliente> => {
    const endpoint = createUrlWithCenterId('/api/clientes');
    return (await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(cliente),
    })) as Cliente;
  },

  // Actualizar cliente
  updateCliente: async (id: number, cliente: Partial<Cliente>): Promise<void> => {
    const endpoint = createUrlWithCenterId(`/api/clientes/${id}`);
    await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(cliente),
    });
  },

  // Eliminar cliente (borrado lógico)
  deleteCliente: async (id: number): Promise<{ message: string }> => {
    const endpoint = createUrlWithCenterId(`/api/clientes/${id}`);
    return (await apiRequest(endpoint, {
      method: 'DELETE',
    })) as { message: string };
  },

  // Reactivar cliente
  reactivarCliente: async (id: number): Promise<{ message: string }> => {
    const endpoint = createUrlWithCenterId(`/api/clientes/${id}/reactivar`);
    return (await apiRequest(endpoint, {
      method: 'PUT',
    })) as { message: string };
  },

  // Clientes asignados a un usuario (UserClientAccess)
  getClientesByUsuario: async (userId: number): Promise<Cliente[]> => {
    const rows = await apiRequest(`/api/clientes/vendedor/${userId}`);
    return (rows || []).map(normalizeCliente);
  },

  // Usuarios con acceso al cliente (UserClientAccess)
  getUsuariosAsignados: async (clienteId: number): Promise<User[]> => {
    const rows = await apiRequest(`/api/clientes/${clienteId}/usuarios-asignados`);
    return (rows || []).map((u: any) => ({
      id: u.id ?? u.Id,
      alias: u.alias ?? u.Alias ?? '',
      email: u.mail ?? u.Mail ?? u.email ?? '',
      activo: u.activo ?? u.Activo ?? true,
      roleId: u.roleId ?? u.RoleId ?? 0,
      nombre: u.nombre ?? u.Nombre,
      apellido: u.apellido ?? u.Apellido,
      centers: [],
      permissions: [],
    }));
  },

  // Obtener clientes por centro
  getClientesByCentro: async (centroId: number): Promise<Cliente[]> => {
    return await apiRequest(`/api/clientes/centro/${centroId}`);
  },

  // Obtener tipos de cliente
  getTiposCliente: async (): Promise<TipoDeCliente[]> => {
    return await apiRequest('/api/clientes/tipos-cliente');
  },

  // Obtener tipos de servicio
  getTiposServicio: async (): Promise<TipoDeServicio[]> => {
    return await apiRequest('/api/clientes/tipos-servicio');
  },

  // Obtener centros
  getCentros: async (): Promise<Center[]> => {
    const raw = await apiRequest('/api/clientes/centros');
    // Normaliza campos desde la API (Name/Description/Active) al modelo del front (nombre/descripcion/activo)
    return (raw || []).map((c: any) => ({
      id: c.id,
      nombre: c.nombre ?? c.name ?? c.Nombre ?? '',
      descripcion: c.descripcion ?? c.description ?? c.Descripcion ?? '',
      activo: c.activo ?? c.active ?? true,
    }));
  },

  // Obtener tipos de producto
  getTiposProducto: async (): Promise<TipoProducto[]> => {
    return await apiRequest('/api/clientes/tipos-producto');
  },

  // Obtener periodicidades
  getPeriodicidades: async (): Promise<Periodicidad[]> => {
    return await apiRequest('/api/clientes/periodicidades');
  },
};
